import { quebecTaxFromPreTax, taxeHebergementAmountForNights } from '../../lib/quebecSalesTax';

export type GuestReservationFormShape = {
  checkIn: string;
  checkOut: string;
  duration: number;
  adults: number;
  children: number;
  paymentMethod: string;
  couponCode?: string;
};

export type VumPromoShape = {
  nights: number;
  forfaitPrice: number;
};

/** Nights billed for an accommodation stay (aligns with cart checkout when dates are incomplete). */
export function guestReservationLodgingNights(
  itemType: 'accommodation' | 'activity' | 'package',
  formData: Pick<GuestReservationFormShape, 'checkIn' | 'checkOut' | 'duration'>,
): number {
  if (itemType !== 'accommodation') return 0;
  if (formData.checkIn && formData.checkOut) {
    const nights = Math.ceil(
      (new Date(formData.checkOut).getTime() - new Date(formData.checkIn).getTime()) /
        (1000 * 60 * 60 * 24),
    );
    return Math.max(0, nights);
  }
  return Math.max(0, formData.duration || 1);
}

export type GuestReservationPricing = {
  basePrice: number;
  bookedNights: number;
  lodgingNights: number;
  taxeHebergement: number;
  gst: number;
  qst: number;
  totalWithTax: number;
  extraNights: number;
  includedNights: number;
  duration: number;
};

export function computeGuestReservationPricing(
  itemType: 'accommodation' | 'activity' | 'package',
  itemPrice: number,
  formData: GuestReservationFormShape,
  vumPromo?: VumPromoShape | null,
  taxeHebergementEnabled = true,
): GuestReservationPricing {
  const totalPersons = (formData.adults ?? 1) + (formData.children ?? 0);
  const bookedNights = guestReservationLodgingNights(itemType, formData);
  const extraNights = vumPromo ? Math.max(0, bookedNights - vumPromo.nights) : bookedNights;
  const includedNights = vumPromo ? Math.min(bookedNights, vumPromo.nights) : 0;

  let basePrice = 0;
  if (vumPromo && itemType === 'accommodation' && bookedNights > 0) {
    basePrice = extraNights * itemPrice;
  } else if (itemType === 'accommodation' && bookedNights > 0) {
    basePrice = itemPrice * bookedNights;
  } else if (itemType === 'activity') {
    basePrice = itemPrice * totalPersons;
  } else {
    basePrice = itemPrice;
  }

  const lodgingNights = itemType === 'accommodation' && bookedNights > 0 ? bookedNights : 0;
  const thEnabled = itemType === 'accommodation' && taxeHebergementEnabled !== false;
  const taxeHebergement = taxeHebergementAmountForNights(lodgingNights, thEnabled);
  const qTax = quebecTaxFromPreTax(Math.max(0, basePrice + taxeHebergement));

  return {
    basePrice,
    bookedNights,
    lodgingNights,
    taxeHebergement,
    gst: qTax.tps,
    qst: qTax.tvq,
    totalWithTax: qTax.total,
    extraNights,
    includedNights,
    duration: bookedNights > 0 ? bookedNights : formData.duration || 1,
  };
}

export function buildGuestReservationPayload(
  itemType: 'accommodation' | 'activity' | 'package',
  itemPrice: number,
  formData: GuestReservationFormShape,
  paymentMode: 'guest' | 'member',
  isLoggedIn: boolean,
  shoppingCartGroups?: Array<{ id: string; adults: number; kids: number }>,
  vumPromo?: VumPromoShape | null,
  taxeHebergementEnabled = true,
) {
  const pricing = computeGuestReservationPricing(
    itemType,
    itemPrice,
    formData,
    vumPromo,
    taxeHebergementEnabled,
  );
  const totalPrice = pricing.basePrice;
  const duration = pricing.duration;

  const isGuest = paymentMode === 'guest' || !isLoggedIn;
  const groups = shoppingCartGroups || undefined;

  const isFullPayment = formData.paymentMethod === 'full' || formData.paymentMethod === 'credit';
  const isPartialPayment = formData.paymentMethod === 'partial';

  const gstAmount = pricing.gst;
  const qstAmount = pricing.qst;
  const taxeHebergementAmount = pricing.taxeHebergement;
  const totalWithTax = pricing.totalWithTax;

  const depositAmount = isPartialPayment ? Math.round(totalWithTax * 0.30 * 100) / 100 : totalWithTax;
  const balanceAmount = isPartialPayment ? Math.round((totalWithTax - depositAmount) * 100) / 100 : 0;

  const finalPaymentStatus: 'pending' | 'paid' = isFullPayment ? 'paid' : 'pending';
  const finalStatus: 'pending' | 'confirmed' = isFullPayment ? 'confirmed' : 'pending';

  return {
    totalPrice,
    totalWithTax,
    gstAmount,
    qstAmount,
    taxeHebergementAmount,
    depositAmount,
    balanceAmount,
    duration,
    isGuest,
    groups,
    finalPaymentStatus,
    finalStatus,
    totalGuests: (formData.adults ?? 1) + (formData.children ?? 0),
  };
}
